/**
 * MonthSummary — analysis affordance shown under the calendar's month
 * header.
 *
 * The existing month-stat strip (rendered inside RidesCalendar) already
 * shows counts/hours/km/TSS. This component adds the LLM coach layer:
 *   - If a monthly summary is cached → 2-3 sentence preview + "Open full
 *     month →" link to /months/<iso>.
 *   - If no summary cached and the month has rides → "Analyze this month"
 *     button that fires the generate mutation.
 *   - During generation → loading state.
 *   - Empty months → render nothing (skip the API call entirely).
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGenerateMonthlySummary,
  useMonthlySummary,
} from '../hooks/useMonthlySummary.ts';
import { GarminAiAttribution } from './GarminAttribution.tsx';
import { previewFromSummary } from '../lib/weeklySummaryPreview.ts';

interface MonthSummaryProps {
  /** 0-indexed calendar month (matches RidesCalendar's cursor.month). */
  year: number;
  month: number;
  /** Total ride count in this calendar month — for the empty-month skip. */
  monthRideCount: number;
}

/** Render a 0-indexed month as the first-of-month ISO date (YYYY-MM-01). */
function firstOfMonthIso(year: number, month: number): string {
  const mm = String(month + 1).padStart(2, '0');
  return `${year}-${mm}-01`;
}

export function MonthSummary({
  year,
  month,
  monthRideCount,
}: Readonly<MonthSummaryProps>) {
  const { t } = useTranslation();
  const iso = firstOfMonthIso(year, month);
  const shouldFetch = monthRideCount > 0;
  const summaryQuery = useMonthlySummary(shouldFetch ? iso : null);
  const generate = useGenerateMonthlySummary();

  // Empty months render nothing — the existing month-stat strip already
  // tells the story, and there's nothing to analyze.
  if (monthRideCount === 0) {
    return null;
  }

  const cached = summaryQuery.data?.summary ?? null;
  const isGenerating =
    generate.isPending && generate.variables === iso;
  const preview = cached ? previewFromSummary(cached) : null;

  return (
    <div className="rounded-lg bg-(--color-bg-tertiary)/40 border border-(--color-border)/60 px-3 py-2 flex flex-col gap-1.5 text-[11px] sm:text-xs text-(--color-text-secondary)">
      {preview ? (
        <div className="flex flex-col gap-1">
          <p className="text-(--color-text-primary) text-xs sm:text-sm leading-snug">
            {preview}
          </p>
          <GarminAiAttribution sources={summaryQuery.data?.ai_sources} />
          <Link
            to={`/months/${iso}`}
            className="text-emerald-400 hover:text-emerald-300 text-[11px] sm:text-xs font-medium self-start"
          >
            {t('month.openFullMonth')}
          </Link>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-(--color-text-secondary)">
            {t('month.uncachedHint')}
          </span>
          <button
            type="button"
            onClick={() => generate.mutate(iso)}
            disabled={isGenerating}
            className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 text-[11px] sm:text-xs font-medium border border-emerald-400/40 hover:border-emerald-300/60 rounded-full px-3 py-1 transition-colors"
          >
            {isGenerating ? t('month.generating') : t('month.analyzeMonth')}
          </button>
        </div>
      )}
    </div>
  );
}
